import React, { useState, useRef, useEffect } from 'react';
import './SearchableSelect.css';
import { matchesPositionSearch } from '../config/positionKeywords';

/**
 * Custom SearchableSelect - Built from scratch without react-select
 * Search field fixed at top of dropdown
 * Supports smart keyword-based search for enhanced matching
 */
const SearchableSelect = ({
  options,
  value,
  onChange,
  placeholder = "Please select...",
  isRequired = false,
  isDisabled = false,
  name,
  className = "",
  enableSmartSearch = false // Enable keyword-based smart search
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  // Convert options to uniform format
  const formattedOptions = options.map(opt =>
    typeof opt === 'string' ? { value: opt, label: opt } : opt
  );

  // Show search field only if more than 3 options
  const showSearch = formattedOptions.length > 3;

  // Find selected option
  const selectedOption = formattedOptions.find(opt => opt.value === value);

  // Filter options based on search
  const filteredOptions = searchText && showSearch
    ? formattedOptions.filter(opt => {
        // Use smart search if enabled (keyword matching)
        if (enableSmartSearch) {
          return matchesPositionSearch(searchText, opt.label);
        }
        // Default: simple text includes
        return opt.label.toLowerCase().includes(searchText.toLowerCase());
      })
    : formattedOptions;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchText('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens (only if search is shown)
  useEffect(() => {
    if (isOpen && showSearch && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen, showSearch]);

  const handleToggle = () => {
    if (!isDisabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setSearchText('');
      }
    }
  };

  const handleSelect = (option) => {
    // Create synthetic event object to match FormWizard's handleChange expectations
    onChange({
      target: {
        name: name,
        value: option.value
      }
    });
    setIsOpen(false);
    setSearchText('');
  };

  return (
    <div
      ref={containerRef}
      className={`searchable-select ${className} ${isDisabled ? 'disabled' : ''}`}
    >
      {/* Select Control - Always clickable to toggle */}
      <div
        className={`select-control ${isOpen ? 'open' : ''}`}
        onClick={handleToggle}
      >
        {showSearch && isOpen ? (
          <input
            ref={searchInputRef}
            type="text"
            className="select-search-input"
            placeholder="Type to search..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            autoFocus
          />
        ) : (
          <div className="select-value">
            {selectedOption ? selectedOption.label : placeholder}
          </div>
        )}
        <div className="select-arrow">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
            <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
          </svg>
        </div>
      </div>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="select-menu">
          {/* Options List */}
          <div className="select-options">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <div
                  key={option.value}
                  className={`select-option ${
                    option.value === value ? 'selected' : ''
                  }`}
                  onClick={() => handleSelect(option)}
                >
                  {option.label}
                </div>
              ))
            ) : (
              <div className="select-no-options">No options found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
